import { Global } from "@emotion/react";
import * as React from "react";
import { createRoot } from "react-dom/client";
import App from "./App";
import { BrowserRouter } from "react-router-dom";
import { createTheme, StyledEngineProvider, ThemeOptions, ThemeProvider } from "@mui/material/styles";
import ScopedCssBaseline from "@mui/material/ScopedCssBaseline";
import { AppInsightsContextProvider } from "./services/appInsights";
import { environment } from "./config/environment";
import { messageParent } from "./taskpane";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useMemo } from "react";

declare module "@mui/material/Button" {
  interface ButtonPropsColorOverrides {
    gradient: true;
  }
}

/* global Office */

const title = "Attelas";

const rootElement: HTMLElement | null = document.getElementById("container");
const root = rootElement ? createRoot(rootElement) : undefined;

// Create light theme configuration
const lightTheme: ThemeOptions = {
  palette: {
    mode: "light",
    grey: {
      "50": "#FFFFFF",
      "100": "#FAFCFE",
      "200": "#ECEFF3",
      "300": "#C7C6D2",
      "400": "#9A98AE",
      "500": "#545365",
      "600": "#222131",
      "700": "#151520",
      "800": "#0D0D14",
      "900": "#000000",
    },
    common: {
      black: "#000000",
      white: "#FFFFFF",
    },
    primary: {
      main: "#005AF8",
    },
    secondary: {
      main: "#BAE8BE",
      contrastText: "#221970",
    },
    success: {
      main: "#DCF8F7",
      contrastText: "#222131",
    },
    info: {
      main: "#ECF5FF",
      contrastText: "#015AF8",
    },
    warning: {
      main: "#FFEED5",
      contrastText: "#222131",
    },
    error: {
      main: "#FDD7E7",
      contrastText: "#760000",
    },
    text: {
      primary: "#222131",
      secondary: "#545365",
      disabled: "#545365",
    },
    divider: "#C7C6D2",
    background: {
      default: "#ECEFF3",
    },
  },
  typography: {
    h1: {
      fontSize: "25px",
      fontWeight: "700",
      lineHeight: "56px",
      letterSpacing: 0,
    },
    h2: {
      fontSize: "18px",
      fontWeight: "700",
      fontFamily: '"IBM Plex Sans", sans-serif',
      letterSpacing: "-0.02em",
      lineHeight: "48px",
    },
    h3: {
      fontSize: "16px",
      fontWeight: "700",
      fontFamily: '"IBM Plex Sans", sans-serif',
      lineHeight: "32px",
      letterSpacing: "-0.02em",
    },
    h4: {
      fontSize: "15px",
      fontWeight: "700",
      fontFamily: '"IBM Plex Sans", sans-serif',
      lineHeight: "24px",
      letterSpacing: "-0.02em",
    },
    subtitle1: {
      fontSize: "16px",
    },
    body1: {
      fontSize: "13px",
      fontWeight: "400",
      fontFamily: '"IBM Plex Sans", sans-serif',
      letterSpacing: 0,
    },
    body2: {
      fontSize: "12px",
      fontWeight: "400",
      fontFamily: '"IBM Plex Sans", sans-serif',
      letterSpacing: 0,
    },
    caption: {
      fontSize: "12px",
      fontWeight: "400",
      fontFamily: '"IBM Plex Sans", sans-serif',
      letterSpacing: 0,
    },
    htmlFontSize: 12,
    fontFamily: '"IBM Plex Sans", sans-serif',
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          fontFamily: '"IBM Plex Sans", sans-serif',
          fontSize: "14px",
          lineHeight: "18px",
          borderRadius: "2px",
          textTransform: "none",
          boxShadow: "none",
          "&>span > svg": {
            transition: "all 0.3s ease-in-out",
            marginLeft: 0,
          },
          "&:hover>span > svg": {
            transition: "all 0.3s ease-in-out",
            marginLeft: "5px",
          },
        },
        contained: {
          "&.MuiButton-colorGradient:not(.Mui-disabled)": {
            color: "#FFFFFF",
            background: "linear-gradient(90deg, #005AF8, #9A1BF9)",
          },
        },
        outlined: {
          "&.MuiButton-colorGradient:not(.Mui-disabled)": {
            "@property --gradient-bg-color": {
              syntax: `"<color>"`,
              initialValue: "#FFFFFF",
              inherits: "false",
            },
            "@property --gradient-start-color": {
              syntax: `"<color>"`,
              initialValue: "#005AF880",
              inherits: "false",
            },
            "@property --gradient-end-color": {
              syntax: `"<color>"`,
              initialValue: "#9A1BF980",
              inherits: "false",
            },

            "--gradient-bg-color": "#FFFFFF",
            "--gradient-start-color": "#005AF880",
            "--gradient-end-color": "#9A1BF980",

            background:
              "linear-gradient(90deg, #005AF8, #9A1BF9) text, linear-gradient(var(--gradient-bg-color), var(--gradient-bg-color)) padding-box, linear-gradient(90deg, var(--gradient-start-color), var(--gradient-end-color)) border-box",
            color: "transparent",
            borderColor: "transparent",
            transition:
              "--gradient-bg-color 0.25s ease-in-out, --gradient-start-color 0.25s ease-in-out, --gradient-end-color 0.25s ease-in-out",

            "&:hover": {
              "--gradient-bg-color": "#F5F8FE",
              "--gradient-start-color": "#005AF8",
              "--gradient-end-color": "#9A1BF9",
            },
          },
        },
      },
    },
    MuiMenu: {
      styleOverrides: {
        paper: {
          border: "1px solid #000",
          boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.1)",
          width: "100%",
        },
        root: {
          "& .MuiMenuItem-root:last-child": {
            borderBottom: "none",
          },
        },
        list: {
          padding: "0",
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          borderBottom: "1px solid #000",
          transition: "all 0.3s ease-in-out",
          "&:hover": {
            backgroundColor: "#d5d2d2",
          },
        },
      },
    },
    MuiAccordionDetails: {
      styleOverrides: {
        root: {
          padding: "0",
        },
      },
    },
    MuiAccordionSummary: {
      styleOverrides: {
        root: {
          borderTop: "1px solid #000",
          padding: "0",
          background: "#fff",
          "&.Mui-expanded": {
            minHeight: "48px",
          },
        },
        content: {
          margin: "0",
          "&.Mui-expanded": {
            margin: "0",
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: "8px",
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          boxShadow: "none",
          borderRadius: "0",
          backgroundColor: "#f7f7f7",
        },
      },
    },
    MuiCardActionArea: {
      styleOverrides: {
        root: {
          "&.paper-style": {
            border: "1px solid #c7c7c7",
            borderRadius: "4px",
          },
          "&:not(.paper-style)": {
            borderRadius: "4px",
            "&:not(.Mui-disabled)": {
              background:
                "linear-gradient(273.1deg, #ED25C1 2.56%, #A74FFF 21.85%, #F096FF 33.42%, #FFFFFF 51.26%, #FFFFFF 54.64%, #FF96E2 74.41%, #874FFF 84.53%, #4D25ED 95.14%)",
              backgroundPosition: "0",
              padding: "2px",
              transition: "all 0.3s ease-in-out",
              "&:hover": {
                backgroundPosition: "100px",
              },
              "& > *:first-child": {
                background: "#fff",
                display: "block",
                borderRadius: "4px",
                "& > *": {
                  background: "linear-gradient(90deg, #4D25ED 0%, #ED25C1 100%)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  color: "#b32ed4",
                },
              },
            },
            "&.Mui-disabled": {
              background: "#000",
              padding: "2px",
              "& > *:first-child": {
                background: "#fff",
                display: "block",
                borderRadius: "4px",
              },
            },
          },
        },
      },
    },
    MuiCheckbox: {
      styleOverrides: {
        colorPrimary: {
          color: "#000000",
          "&.Mui-checked": {
            color: "#000000",
          },
          "&.MuiCheckbox-indeterminate": {
            color: "#000000",
          },
          "&.Mui-disabled": {
            color: "rgba(0, 0, 0, 0.26)",
          },
        },
      },
    },
    MuiAlert: {
      styleOverrides: {
        root: {
          borderLeft: "4px solid",
        },
        standardInfo: {
          color: "#222131",
          borderLeftColor: "#015AF8",
          backgroundColor: "#ECF5FF",

          "& .MuiAlert-icon": {
            color: "#015AF8",
          },

          "& .MuiAlertTitle-root": {
            color: "#015AF8",
          },
        },
        standardSuccess: {
          color: "#222131",
          borderLeftColor: "#37C7AA",
          backgroundColor: "#DCF8F7",

          "& .MuiAlert-icon": {
            color: "#37C7AA",
          },
        },
        standardWarning: {
          color: "#222131",
          borderLeftColor: "#E2AC00",
          backgroundColor: "#FFEED5",

          "& .MuiAlert-icon": {
            color: "#E2AC00",
          },
        },
        standardError: {
          color: "#760000",
          borderLeftColor: "#760000",
          backgroundColor: "#FDD7E7",

          "& .MuiAlert-icon": {
            color: "#760000",
          },
        },
      },
    },
  },
};

// Create dark theme configuration
const darkTheme: ThemeOptions = {
  palette: {
    mode: "dark",
    grey: {
      "50": "#0D0D0D",
      "100": "#1A1A1A",
      "200": "#2D2D2D",
      "300": "#424242",
      "400": "#757575",
      "500": "#BDBDBD",
      "600": "#E0E0E0",
      "700": "#EEEEEE",
      "800": "#F5F5F5",
      "900": "#FFFFFF",
    },
    common: {
      black: "#1E1E1E",
      white: "#FFFFFF",
    },
    primary: {
      main: "#90CAF9", // Lighter blue for dark mode
    },
    secondary: {
      main: "#69F0AE",
      contrastText: "#263238",
    },
    success: {
      main: "#1DE9B6",
      contrastText: "#263238",
    },
    info: {
      main: "#29B6F6",
      contrastText: "#FFFFFF",
    },
    warning: {
      main: "#FFD54F",
      contrastText: "#263238",
    },
    error: {
      main: "#FF8A80",
      contrastText: "#263238",
    },
    text: {
      primary: "#FFFFFF",
      secondary: "#B0BEC5",
      disabled: "#78909C",
    },
    divider: "#424242",
    background: {
      default: "#121212",
      paper: "#1E1E1E",
    },
  },
  // Keep the same typography as light theme
  typography: lightTheme.typography,
  // Customize components for dark mode
  components: {
    ...lightTheme.components,
    MuiPaper: {
      styleOverrides: {
        root: {
          boxShadow: "none",
          borderRadius: "0",
          backgroundColor: "#1E1E1E",
        },
      },
    },
    MuiAccordionSummary: {
      styleOverrides: {
        root: {
          borderTop: "1px solid #424242",
          padding: "0",
          background: "#1E1E1E",
          "&.Mui-expanded": {
            minHeight: "48px",
          },
        },
        content: {
          margin: "0",
          "&.Mui-expanded": {
            margin: "0",
          },
        },
      },
    },
    MuiAutocomplete: {
      styleOverrides: {
        root: {
          "& .MuiFormControl-root": {
            background: "#1E1E1E",
          },
        },
      },
    },
    MuiCardActionArea: {
      styleOverrides: {
        root: {
          "&.paper-style": {
            border: "1px solid #424242",
            borderRadius: "4px",
            color: "#fff",
          },
          "&:not(.paper-style)": {
            borderRadius: "4px",
            "&:not(.Mui-disabled)": {
              background:
                "linear-gradient(273.1deg, #ED25C1 2.56%, #A74FFF 21.85%, #F096FF 33.42%, #424242 51.26%, #424242 54.64%, #FF96E2 74.41%, #874FFF 84.53%, #4D25ED 95.14%)",
              backgroundPosition: "0",
              padding: "2px",
              transition: "all 0.3s ease-in-out",
              "&:hover": {
                backgroundPosition: "100px",
              },
              "& > *:first-child": {
                background: "#424242",
                display: "block",
                borderRadius: "4px",
                "& > *": {
                  background: "#fff",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  color: "#fff",
                },
              },
            },
            "&.Mui-disabled": {
              background: "#000",
              padding: "2px",
              "& > *:first-child": {
                background: "#424242",
                display: "block",
                borderRadius: "4px",
              },
            },
          },
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          borderBottom: "1px solid #424242",
          transition: "all 0.3s ease-in-out",
          "&:hover": {
            backgroundColor: "#2D2D2D",
          },
        },
      },
    },
    MuiMenu: {
      styleOverrides: {
        paper: {
          border: "1px solid #424242",
          boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.3)",
          width: "100%",
        },
        root: {
          "& .MuiMenuItem-root:last-child": {
            borderBottom: "none",
          },
        },
        list: {
          padding: "0",
        },
      },
    },
    MuiCheckbox: {
      styleOverrides: {
        colorPrimary: {
          color: "#FFFFFF",
          "&.Mui-checked": {
            color: "#90CAF9",
          },
          "&.MuiCheckbox-indeterminate": {
            color: "#90CAF9",
          },
          "&.Mui-disabled": {
            color: "rgba(255, 255, 255, 0.3)",
          },
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: "#424242",
          color: "#FFFFFF",
          fontSize: "12px",
        },
        arrow: {
          color: "#424242",
        },
      },
    },
    MuiAlert: {
      styleOverrides: {
        root: {
          borderLeft: "4px solid",
        },
        standardInfo: {
          color: "#29B6F6",
          borderLeftColor: "#29B6F6",
          backgroundColor: "rgba(41, 182, 246, 0.12)",
          "& .MuiAlert-icon": {
            color: "#29B6F6",
          },
          "& .MuiAlertTitle-root": {
            color: "#29B6F6",
          },
        },
        standardSuccess: {
          color: "#69F0AE",
          borderLeftColor: "#69F0AE",
          backgroundColor: "rgba(105, 240, 174, 0.12)",
          "& .MuiAlert-icon": {
            color: "#69F0AE",
          },
          "& .MuiAlertTitle-root": {
            color: "#69F0AE",
          },
        },
        standardWarning: {
          color: "#FFD54F",
          borderLeftColor: "#FFD54F",
          backgroundColor: "rgba(255, 213, 79, 0.12)",
          "& .MuiAlert-icon": {
            color: "#FFD54F",
          },
          "& .MuiAlertTitle-root": {
            color: "#FFD54F",
          },
        },
        standardError: {
          color: "#FF8A80",
          borderLeftColor: "#FF8A80",
          backgroundColor: "rgba(255, 138, 128, 0.12)",
          "& .MuiAlert-icon": {
            color: "#FF8A80",
          },
          "& .MuiAlertTitle-root": {
            color: "#FF8A80",
          },
        },
      },
    },
  },
};

function Index() {
  const prefersDarkMode = useMediaQuery("(prefers-color-scheme: dark)");
  const localStorageTheme = localStorage.getItem("theme");
  const actuallyPrefersDarkMode = !localStorageTheme
    ? prefersDarkMode
      ? true
      : false
    : localStorageTheme === "dark"
      ? true
      : false;

  const theme = useMemo(() => createTheme(actuallyPrefersDarkMode ? darkTheme : lightTheme), [actuallyPrefersDarkMode]);

  return (
    <StyledEngineProvider injectFirst>
      <ThemeProvider theme={theme}>
        <Global styles={{ html: { background: actuallyPrefersDarkMode ? "#121212" : "#fff" } }} />
        <ScopedCssBaseline
          style={{
            display: "flex",
            flexDirection: "column",
            height: "100%",
            background: actuallyPrefersDarkMode ? "#121212" : "#fff",
          }}
        >
          <AppInsightsContextProvider instrumentationKey={environment.instrumentationKey}>
            <BrowserRouter>
              <App title={title} />
            </BrowserRouter>
          </AppInsightsContextProvider>
        </ScopedCssBaseline>
      </ThemeProvider>
    </StyledEngineProvider>
  );
}

/* Render application after Office initializes */
Office.onReady(() => {
  const searchParams = new URLSearchParams(window.location.search);
  const isAuthRedirect = searchParams.has("code") && searchParams.has("state");
  if (isAuthRedirect) {
    messageParent(window.location.href);
  } else {
    root?.render(<Index />);
  }
});

if ((module as any).hot) {
  (module as any).hot.accept("./App", () => {
    root?.render(<Index />);
  });

  (module as any).hot.accept();
}
